class CustomDropdown extends HTMLElement {
  constructor() {
    super();
    // Bind methods
    this._onDocumentClick = this._onDocumentClick.bind(this);
  // Handler containers for proper removal
  this._optionHandlers = new Map();
  this._closeHandler = null;
  this._keydownHandler = null;
  }

  connectedCallback() {
    // Elements inside the custom element
    this._toggle = this.querySelector('.custom-dropdown__toggle');
    this._selected = this.querySelector('.custom-dropdown__selected');
    this._options = Array.from(this.querySelectorAll('.custom-dropdown__option'));
    this._closeBtn = this.querySelector('.custom-dropdown__close');
    this._overlay = this.querySelector('.custom-dropdown__overlay');
    this._container = this.querySelector('.custom-dropdown__container');
    this._currentFocusIndex = -1;

    // Defensive: nothing to do
    if (!this._toggle || !this._selected) return;

    // Setup ARIA attributes for dropdown pattern
    this._toggle.setAttribute('aria-haspopup', 'listbox');
    this._toggle.setAttribute('aria-expanded', 'false');
    if (this._selected && this._selected.id) {
      this._toggle.setAttribute('aria-labelledby', this._selected.id);
    }

    // Container already has role="listbox" and aria-label from template
    // Just ensure toggle is connected to listbox
    if (this._container && this._container.id) {
      this._toggle.setAttribute('aria-controls', this._container.id);
    }

    // Setup options with proper ARIA - ensure they're properly configured
    this._options.forEach((opt, index) => {
      opt.setAttribute('role', 'option');
      // aria-selected should already be set in template, but ensure it's correct
      const currentlySelected = opt.classList.contains('custom-dropdown__option--selected');
      opt.setAttribute('aria-selected', currentlySelected ? 'true' : 'false');
      opt.setAttribute('tabindex', '-1');
      if (!opt.dataset.index) {
        opt.dataset.index = index;
      }
    });

    // Attach option listeners and keep references so we can remove them
    this._optionHandlers = new Map();
    this._options.forEach(opt => {
      const handler = (e) => this._onOptionClick(e, opt);
      this._optionHandlers.set(opt, handler);
      opt.addEventListener('click', handler);
    });

    // Toggle dropdown on trigger click
    this._toggleHandler = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (this._toggle) {
        this._toggle.checked = !this._toggle.checked;
        this._toggle.setAttribute('aria-expanded', this._toggle.checked ? 'true' : 'false');
      }
    };
    if (this._selected) this._selected.addEventListener('click', this._toggleHandler);

    // Close interactions
    this._closeHandler = () => this._close();
    if (this._closeBtn) this._closeBtn.addEventListener('click', this._closeHandler);
    if (this._overlay) this._overlay.addEventListener('click', this._closeHandler);

    // Close when clicking outside
    document.addEventListener('click', this._onDocumentClick);

    // Keyboard handling for accessibility
    this._keydownHandler = (e) => this._onKeyDown(e);
    this.addEventListener('keydown', this._keydownHandler);
  }

  disconnectedCallback() {
    // Remove option listeners
    if (this._optionHandlers && this._optionHandlers.size) {
      this._optionHandlers.forEach((handler, opt) => opt.removeEventListener('click', handler));
      this._optionHandlers.clear();
    }

    // Remove toggle listener
    if (this._selected && this._toggleHandler) this._selected.removeEventListener('click', this._toggleHandler);

    // Remove close/overlay listeners
    if (this._closeBtn && this._closeHandler) this._closeBtn.removeEventListener('click', this._closeHandler);
    if (this._overlay && this._closeHandler) this._overlay.removeEventListener('click', this._closeHandler);

    // Remove outside click listener
    document.removeEventListener('click', this._onDocumentClick);

    // Remove keyboard listener
    if (this._keydownHandler) this.removeEventListener('keydown', this._keydownHandler);
  }

  _onOptionClick(event, optionEl) {
    event.preventDefault();
    event.stopPropagation();

    // Check if option contains a link
    const link = optionEl.querySelector('a');
    const value = link ? link.getAttribute('data-value') : optionEl.getAttribute('data-value');
    const label = link ? link.getAttribute('data-label') : optionEl.getAttribute('data-label');

    // Remove selected class and aria from all options
    this._options.forEach(o => {
      o.classList.remove('custom-dropdown__option--selected');
      o.setAttribute('aria-selected', 'false');
    });
    optionEl.classList.add('custom-dropdown__option--selected');
    optionEl.setAttribute('aria-selected', 'true');

    // Preserve arrow SVG if present
    const arrow = this._selected.querySelector('.custom-dropdown__arrow');
    const arrowHtml = arrow ? arrow.outerHTML : '';

    // Update selected text while preserving arrow
    const displayText = label || optionEl.textContent.trim();
    this._selected.innerHTML = this._escapeHtml(displayText) + arrowHtml;
    this._selected.classList.remove('placeholder');

    // Close the dropdown (uncheck toggle) and update ARIA
    if (this._toggle) {
      this._toggle.checked = false;
      this._toggle.setAttribute('aria-expanded', 'false');
    }

    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('dropdown-change', {
      detail: { value, label: displayText, dropdown: this, link: link },
      bubbles: true
    }));
  }

  _close() {
    if (this._toggle) {
      this._toggle.checked = false;
      this._toggle.setAttribute('aria-expanded', 'false');
    }
    this._currentFocusIndex = -1;
  }

  _onDocumentClick(e) {
    if (!this.contains(e.target)) this._close();
  }

  _onKeyDown(e) {
    // Only handle keyboard when dropdown is open
    const isOpen = this._toggle && this._toggle.checked;

    if (e.key === 'Escape') {
      this._close();
      if (this._toggle) this._toggle.focus();
      return;
    }

    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        this._focusNextOption();
        break;
      case 'ArrowUp':
        e.preventDefault();
        this._focusPreviousOption();
        break;
      case 'Home':
        e.preventDefault();
        this._currentFocusIndex = 0;
        this._updateFocus();
        break;
      case 'End':
        e.preventDefault();
        this._currentFocusIndex = this._options.length - 1;
        this._updateFocus();
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (this._currentFocusIndex >= 0) {
          this._onOptionClick(new MouseEvent('click'), this._options[this._currentFocusIndex]);
        }
        break;
    }
  }

  _focusNextOption() {
    if (this._currentFocusIndex < this._options.length - 1) {
      this._currentFocusIndex++;
    } else {
      this._currentFocusIndex = 0;
    }
    this._updateFocus();
  }

  _focusPreviousOption() {
    if (this._currentFocusIndex > 0) {
      this._currentFocusIndex--;
    } else {
      this._currentFocusIndex = this._options.length - 1;
    }
    this._updateFocus();
  }

  _updateFocus() {
    this._options.forEach((opt, idx) => {
      if (idx === this._currentFocusIndex) {
        opt.setAttribute('tabindex', '0');
        opt.focus();
      } else {
        opt.setAttribute('tabindex', '-1');
      }
    });
  }

  _escapeHtml(unsafe) {
    return unsafe
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
}

// Define the custom element if not already defined
if (typeof window !== 'undefined' && !customElements.get('custom-dropdown')) {
  customElements.define('custom-dropdown', CustomDropdown);
}

// Optional: expose a helper to initialize dropdowns manually (rarely needed)
window.CustomDropdown = CustomDropdown;
